import type { OrderStatus } from '@/types/orderStatus';
import { ORDER_IN_FLIGHT_STATUSES } from '@/types/orderStatus';

export type NormalizedStatus = OrderStatus | string;

export interface DemoOrderModifier {
  price_delta_snapshot_cents?: number;
  price_delta_cents?: number;
  price_delta?: number;
  priceDeltaCents?: number;
}

export interface DemoOrderItem {
  item_id?: string;
  item_name?: string;
  quantity?: number;
  price_snapshot_cents?: number;
  price_cents?: number;
  price?: number;
  order_item_modifiers?: DemoOrderModifier[];
  modifiers?: DemoOrderModifier[];
}

export interface DemoOrder {
  id: string;
  order_code?: string;
  orderCode?: string;
  status?: NormalizedStatus | string;
  placed_at?: string;
  created_at?: string;
  updated_at?: string;
  placedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  total_cents?: number;
  total_amount_cents?: number;
  totalAmountCents?: number;
  total_amount?: number;
  subtotal_cents?: number;
  tax_cents?: number;
  order_items?: DemoOrderItem[];
  demo_order_items?: DemoOrderItem[];
  items?: DemoOrderItem[];
}

function getOrderItems(orderOrItems: DemoOrder | DemoOrderItem[] | undefined): DemoOrderItem[] {
  if (!orderOrItems) return [];
  if (Array.isArray(orderOrItems)) return orderOrItems;
  return (
    orderOrItems.order_items ||
    orderOrItems.demo_order_items ||
    orderOrItems.items ||
    []
  );
}

function itemBaseCents(item: DemoOrderItem): number {
  if (typeof item.price_snapshot_cents === 'number') return item.price_snapshot_cents;
  if (typeof item.price_cents === 'number') return item.price_cents;
  if (typeof item.price === 'number') return Math.round(item.price * 100);
  return 0;
}

function modifierDeltaCents(modifier: DemoOrderModifier): number {
  if (typeof modifier.price_delta_snapshot_cents === 'number') return modifier.price_delta_snapshot_cents;
  if (typeof modifier.price_delta_cents === 'number') return modifier.price_delta_cents;
  if (typeof modifier.priceDeltaCents === 'number') return modifier.priceDeltaCents;
  if (typeof modifier.price_delta === 'number') {
    const delta = modifier.price_delta;
    return Math.abs(delta) < 1 ? Math.round(delta * 100) : delta;
  }
  return 0;
}

function itemTotalCents(item: DemoOrderItem): number {
  const quantity = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
  const base = itemBaseCents(item);
  const modifiers = item.order_item_modifiers || item.modifiers || [];
  const modifierSum = Array.isArray(modifiers)
    ? modifiers.reduce((sum, mod) => sum + modifierDeltaCents(mod), 0)
    : 0;
  return (base + modifierSum) * quantity;
}

const LEGACY_STATUS_MAP: Record<string, OrderStatus> = {
  NEW: 'PENDING_PAYMENT',
  PREPARING: 'IN_PROGRESS',
  VOID: 'VOIDED',
};

export function normalizeStatus(input: unknown): NormalizedStatus {
  const s = String(input ?? '').toUpperCase();
  return (LEGACY_STATUS_MAP[s] ?? (s as NormalizedStatus));
}

const ACTIVE_SET = new Set<NormalizedStatus>(ORDER_IN_FLIGHT_STATUSES);

const FINAL_REVENUE_SET = new Set<NormalizedStatus>(['SERVED', 'PICKED_UP', 'COMPLETED']);

export function isActiveStatus(s: unknown): boolean {
  return ACTIVE_SET.has(normalizeStatus(s));
}

export function isFinalForRevenue(s: unknown): boolean {
  return FINAL_REVENUE_SET.has(normalizeStatus(s));
}

export function getOrderTimestamp(o: DemoOrder): Date {
  const ts =
    o?.placed_at ||
    o?.created_at ||
    o?.updated_at ||
    o?.placedAt ||
    o?.createdAt ||
    o?.updatedAt;
  try {
    return ts ? new Date(ts) : new Date();
  } catch {
    return new Date();
  }
}

export function isTodayLocal(d: Date): boolean {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const ms = d.getTime();
  return ms >= start.getTime() && ms <= end.getTime();
}

export function subtotalAndTaxCents(
  orderOrItems: DemoOrder | DemoOrderItem[] | undefined,
  taxPct = 8.5
): { subtotal_cents: number; tax_cents: number; total_cents: number } {
  const items = getOrderItems(orderOrItems);
  const subtotal = items.reduce((sum, item) => sum + itemTotalCents(item), 0);
  const tax = Math.round(subtotal * (taxPct / 100));
  return {
    subtotal_cents: subtotal,
    tax_cents: tax,
    total_cents: subtotal + tax,
  };
}

export function amountCents(o: DemoOrder): number {
  if (typeof o?.total_cents === 'number' && o.total_cents > 0) return o.total_cents;
  if (typeof o?.total_amount_cents === 'number' && o.total_amount_cents > 0) return o.total_amount_cents;
  if (typeof o?.totalAmountCents === 'number' && o.totalAmountCents > 0) return o.totalAmountCents;
  if (typeof o?.total_amount === 'number' && o.total_amount > 0) return Math.round(o.total_amount * 100);

  const { total_cents } = subtotalAndTaxCents(o);
  return total_cents;
}

export function formatCents(cents: number): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const value = Number.isFinite(cents) ? cents : 0;
  return formatter.format(value / 100);
}

let recentNewLogAt = 0;

export function recentNewOrders(orders: DemoOrder[], limit = 5): DemoOrder[] {
  const source = Array.isArray(orders) ? orders : [];
  const filtered = source.filter((o) => normalizeStatus(o?.status) === 'PENDING_PAYMENT');
  const sorted = [...filtered].sort((a, b) => {
    const aTs = String(a?.placed_at || a?.created_at || a?.updated_at || '');
    const bTs = String(b?.placed_at || b?.created_at || b?.updated_at || '');
    return bTs.localeCompare(aTs);
  });
  const out = sorted.slice(0, limit);

  if (typeof window !== 'undefined' && import.meta.env?.DEV) {
    const now = Date.now();
    if (now - recentNewLogAt > 60_000) {
      console.debug('[kpis] recentNewOrders ->', { in: source.length, out: out.length });
      recentNewLogAt = now;
    }
  }

  return out;
}

export function computeDemoKpis(orders: DemoOrder[]) {
  const normalized: DemoOrder[] = Array.isArray(orders)
    ? orders.map((o) => ({ ...o, status: normalizeStatus(o?.status) }))
    : [];

  const orderCount = normalized.length;
  let todayOrdersCount = 0;
  let kitchenQueueCount = 0;
  let revenueTodayCents = 0;

  for (const o of normalized) {
    const ts = getOrderTimestamp(o);
    const today = isTodayLocal(ts);
    if (today) todayOrdersCount++;

    if (isActiveStatus(o.status)) kitchenQueueCount++;

    if (today && isFinalForRevenue(o.status)) {
      revenueTodayCents += amountCents(o);
    }
  }

  if (typeof window !== 'undefined' && import.meta.env?.DEV) {
    console.debug('[kpis] recompute', { total: normalized.length });
  }

  return {
    orderCount,
    todayOrdersCount,
    kitchenQueueCount,
    revenueTodayCents,
  };
}
